import api from './api';
import { WorkflowConfig, WorkflowStatus, WorkflowLog, ManualTriggerResponse } from '../types/workflow';

// ============================================================================
// WORKFLOW STATUS & MONITORING
// ============================================================================

export const getWorkflowStatus = async (): Promise<{ data: WorkflowStatus }> => {
  return await api.get('/workflows/status');
};

export const getSystemHealth = async (): Promise<{ data: any }> => {
  return await api.get('/workflows/health');
};

export const getWorkflowLogs = async (params?: {
  limit?: number;
  type?: string;
  status?: string;
  from?: string;
  to?: string;
}): Promise<{ data: WorkflowLog[] }> => {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.type) queryParams.append('type', params.type);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.from) queryParams.append('from', params.from);
  if (params?.to) queryParams.append('to', params.to);
  
  return await api.get(`/workflows/logs?${queryParams.toString()}`);
};

// ============================================================================
// MANUAL TRIGGERS
// ============================================================================

export const triggerReminderProcessing = async (force = false): Promise<{ data: ManualTriggerResponse }> => {
  return await api.post('/workflows/trigger/reminders', { force });
};

export const triggerOverdueCheck = async (): Promise<{ data: ManualTriggerResponse }> => {
  return await api.post('/workflows/trigger/overdue-check');
};

export const generateManualReport = async (reportType: 'weekly' | 'yearly' = 'weekly', year?: number): Promise<{ data: ManualTriggerResponse }> => {
  return await api.post('/workflows/trigger/report', { reportType, year });
};

export const triggerManualCleanup = async (dryRun = false): Promise<{ data: ManualTriggerResponse }> => {
  return await api.post('/workflows/trigger/cleanup', { dryRun });
};

// ============================================================================
// CONFIGURATION MANAGEMENT
// ============================================================================

export const updateWorkflowConfig = async (config: Partial<WorkflowConfig>): Promise<{ data: WorkflowConfig }> => {
  return await api.put('/workflows/config', { config });
};

export const getWorkflowConfig = async (): Promise<{ data: WorkflowConfig }> => {
  return await api.get('/workflows/config');
};

// ============================================================================
// SCHEDULE MANAGEMENT
// ============================================================================

export const updateSchedule = async (scheduleType: string, cronExpression: string): Promise<{ data: any }> => {
  return await api.put('/workflows/schedule', { scheduleType, cronExpression });
};

export const validateCronExpression = async (expression: string): Promise<{ data: { valid: boolean; nextRuns?: string[] } }> => {
  return await api.post('/workflows/validate-cron', { expression });
};

// ============================================================================
// TESTING & DEBUGGING
// ============================================================================

export const testEmailConnection = async (): Promise<{ data: { success: boolean; message: string } }> => {
  return await api.post('/workflows/test-email');
};

export const sendTestReminder = async (submissionId: string): Promise<{ data: any }> => {
  return await api.post(`/workflows/test-reminder/${submissionId}`);
};
// ============================================================================
// PERFION SYNC
// ============================================================================

export interface PerfionSyncTask {
  taskName: string;
  taskType: string;
  cronSchedule: string;
  isEnabled: boolean;
  lastExecution?: string;
  lastExecutionStatus?: 'success' | 'error' | 'running';
  lastExecutionDetails?: {
    processed?: number;
    created?: number;
    updated?: number;
    errors?: number;
    message?: string;
    duration?: number;
  };
  nextExecution?: string;
  executionCount: number;
  errorCount: number;
}

export const getPerfionSyncStatus = async (): Promise<{ data: { success: boolean; tasks: PerfionSyncTask[]; timestamp: string } }> => {
  return await api.get('/workflows/perfion-sync-status');
};

export const triggerArticleSync = async (): Promise<{ data: ManualTriggerResponse }> => {
  return await api.post('/workflows/trigger/article-sync');
};

export const triggerSupplierSync = async (): Promise<{ data: ManualTriggerResponse }> => {
  return await api.post('/workflows/trigger/supplier-sync');
};
